import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import styles from './DashboardLayout.module.css';
import { 
  FiHome, FiUsers, FiBriefcase, FiDollarSign, 
  FiTrendingUp, FiCalendar, FiMessageSquare, 
  FiSettings, FiLogOut, FiMenu, FiX,
  FiBarChart2, FiTarget, FiAward, FiClock,
  FiUserCheck, FiFileText, FiMail, FiBell,
  FiGrid, FiPieChart, FiClipboard, FiFlag,
  FiTruck, FiShoppingBag
} from 'react-icons/fi';

const DashboardLayout = ({ children, role }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchUser();
    fetchNotifications();
  }, []);

  const fetchUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      setUser(profile);
    }
  };

  const fetchNotifications = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_read', false)
      .order('created_at', { ascending: false })
      .limit(5);
    setNotifications(data || []);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const menuItems = {
    'Founder & Chairman': [
      { icon: FiHome, label: 'Overview', path: '/dashboard/founder' },
      { icon: FiTarget, label: 'Strategic Vision', path: '/dashboard/founder/vision' },
      { icon: FiBarChart2, label: 'Corporate Performance', path: '/dashboard/founder/performance' },
      { icon: FiUsers, label: 'Board Management', path: '/dashboard/founder/board' },
      { icon: FiTrendingUp, label: 'Investment & Growth', path: '/dashboard/founder/investments' },
      { icon: FiAward, label: 'Corporate Governance', path: '/dashboard/founder/governance' },
      { icon: FiFlag, label: 'Future Roadmap', path: '/dashboard/founder/roadmap' }
    ],
    'MD': [
      { icon: FiHome, label: 'Overview', path: '/dashboard/md' },
      { icon: FiBriefcase, label: 'Business Units', path: '/dashboard/md/units' },
      { icon: FiTarget, label: 'Strategic Goals', path: '/dashboard/md/goals' },
      { icon: FiUsers, label: 'Leadership Team', path: '/dashboard/md/leadership' },
      { icon: FiTrendingUp, label: 'Performance Metrics', path: '/dashboard/md/metrics' },
      { icon: FiCalendar, label: 'Strategic Calendar', path: '/dashboard/md/calendar' },
      { icon: FiClipboard, label: 'Board Reports', path: '/dashboard/md/reports' }
    ],
    'CEO': [
      { icon: FiHome, label: 'Overview', path: '/dashboard/ceo' },
      { icon: FiBarChart2, label: 'Business Analytics', path: '/dashboard/ceo/analytics' },
      { icon: FiUsers, label: 'Department Heads', path: '/dashboard/ceo/heads' },
      { icon: FiTarget, label: 'OKRs', path: '/dashboard/ceo/okrs' },
      { icon: FiTrendingUp, label: 'Revenue & Growth', path: '/dashboard/ceo/revenue' },
      { icon: FiCalendar, label: 'Strategic Planning', path: '/dashboard/ceo/planning' },
      { icon: FiPieChart, label: 'Market Analysis', path: '/dashboard/ceo/market' }
    ],
    'COO': [
      { icon: FiHome, label: 'Overview', path: '/dashboard/coo' },
      { icon: FiClock, label: 'Operations', path: '/dashboard/coo/operations' },
      { icon: FiTruck, label: 'Supply Chain', path: '/dashboard/coo/supply' },
      { icon: FiUsers, label: 'Resource Allocation', path: '/dashboard/coo/resources' },
      { icon: FiCalendar, label: 'Project Timeline', path: '/dashboard/coo/timeline' },
      { icon: FiClipboard, label: 'Process Optimization', path: '/dashboard/coo/process' },
      { icon: FiFileText, label: 'Operational Reports', path: '/dashboard/coo/reports' }
    ],
    'CFO': [
      { icon: FiHome, label: 'Overview', path: '/dashboard/cfo' },
      { icon: FiDollarSign, label: 'Financial Reports', path: '/dashboard/cfo/finance' },
      { icon: FiTrendingUp, label: 'Revenue Analytics', path: '/dashboard/cfo/revenue' },
      { icon: FiBriefcase, label: 'Budget Planning', path: '/dashboard/cfo/budget' },
      { icon: FiPieChart, label: 'Investment Portfolio', path: '/dashboard/cfo/investments' },
      { icon: FiBarChart2, label: 'Forecasting', path: '/dashboard/cfo/forecast' },
      { icon: FiFileText, label: 'Audit & Compliance', path: '/dashboard/cfo/audit' }
    ],
    'Employee': [
      { icon: FiHome, label: 'Overview', path: '/dashboard/employee' },
      { icon: FiClipboard, label: 'My Tasks', path: '/dashboard/employee/tasks' },
      { icon: FiCalendar, label: 'Attendance', path: '/dashboard/employee/attendance' },
      { icon: FiMessageSquare, label: 'Feedback', path: '/dashboard/employee/feedback' },
      { icon: FiBarChart2, label: 'My Performance', path: '/dashboard/employee/performance' },
      { icon: FiFileText, label: 'Leave Requests', path: '/dashboard/employee/leave' },
      { icon: FiUsers, label: 'My Team', path: '/dashboard/employee/team' }
    ]
  };

  const currentMenu = menuItems[role] || menuItems.Employee;

  return (
    <div className={styles.dashboard}>
      {/* Desktop Sidebar */}
      <div className={`${styles.sidebar} ${sidebarOpen ? styles.open : styles.closed}`}>
        <div className={styles.sidebarHeader}>
          <div className={styles.logo}>
            <h2>BrandVerse</h2>
            <span className={styles.roleBadge}>{role}</span>
          </div>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className={styles.sidebarToggle}>
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
        
        <nav className={styles.sidebarNav}>
          {currentMenu.map((item, index) => (
            <button
              key={index}
              onClick={() => navigate(item.path)}
              className={`${styles.navItem} ${location.pathname === item.path ? styles.active : ''}`}
            >
              <item.icon />
              {sidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <button onClick={handleLogout} className={styles.logoutButton}>
            <FiLogOut />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Mobile Menu Button */}
      <button className={styles.mobileMenuBtn} onClick={() => setMobileMenuOpen(true)}>
        <FiMenu />
      </button>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <>
          <div className={styles.mobileOverlay} onClick={() => setMobileMenuOpen(false)} />
          <div className={styles.mobileSidebar}>
            <div className={styles.mobileSidebarHeader}>
              <h2>BrandVerse</h2>
              <button onClick={() => setMobileMenuOpen(false)}><FiX /></button>
            </div>
            <nav>
              {currentMenu.map((item, index) => (
                <button
                  key={index}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className={styles.mobileNavItem}
                >
                  <item.icon />
                  <span>{item.label}</span>
                </button>
              ))}
              <button onClick={handleLogout} className={styles.mobileLogoutBtn}>
                <FiLogOut /> Logout
              </button>
            </nav>
          </div>
        </>
      )}

      {/* Main Content */}
      <div className={`${styles.mainContent} ${!sidebarOpen ? styles.expanded : ''}`}>
        <div className={styles.topBar}>
          <div className={styles.userInfo}>
            <div className={styles.notifications}>
              <FiBell />
              {notifications.length > 0 && (
                <span className={styles.notificationBadge}>{notifications.length}</span>
              )}
            </div>
            <div className={styles.userAvatar}>
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className={styles.userDetails}>
              <span className={styles.userName}>{user?.name || 'User'}</span>
              <span className={styles.userRole}>{role}</span>
            </div>
          </div>
        </div>
        <div className={styles.content}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;